"use strict";

const util = require("js/util.js");
const viewRelations = require("js/editor/view_relations.js");
const viewUtils = require("js/editor/card_view_utils.js");

const SELECTOR_CARD = ".view-card";
const SELECTOR_BORDER = ".border-container path.border";
const SELECTOR_RESIZE_CATCHER = ".border-container path.pointer-event-catcher";

const ADJUST_UP = "up";
const ADJUST_DOWN = "down";
const ADJUST_LEFT = "left";
const ADJUST_RIGHT = "right";

function renderBorder(card) {
    const border = card.querySelector(SELECTOR_BORDER);
    // TODO: Fix me, this is wrong
    if (!border) {
        return;
    }

    let d, classes, bounds, padding;

    switch (card.dataset.borderType) {
        case "line":
            [d, classes, bounds, padding] = lineBorder(card, border);
            break;
        case "double-line":
            [d, classes, bounds, padding] = doubleLineBorder(card, border);
            break;
        case "dashed":
            [d, classes, bounds, padding] = lineBorder(card, border);
            border.setAttribute("stroke-dasharray", "12 8");
            break;
        case "dotted":
            [d, classes, bounds, padding] = lineBorder(card, border);
            border.setAttribute("stroke-dasharray", "0.5 6");
            break;
        case "wavy":
            [d, classes, bounds, padding] = wavyBorder(card);
            break;
        case "jagged":
            [d, classes, bounds, padding] = jaggedBorder(card);
            break;
        default:
            [d, classes, bounds, padding] = lineBorder(card, border);
    }

    border.setAttribute("d", d);
    if (classes != "") {
        border.classList.add(classes);
    }

    card.querySelectorAll(SELECTOR_RESIZE_CATCHER).forEach((part) =>
        renderBorderCatcherPart(part, bounds, 2 * padding),
    );
}

function renderBorderCatcherPart(catcher, bounds, padding) {
    const where = getCatcherLocation(catcher);
    let d;

    let w = Math.ceil(bounds.width / 3);
    let h = Math.ceil(bounds.height / 3);

    switch (where) {
        case "loc-top-left":
            d = `M0,0 h${w} v${padding} h${-(w - padding)} v${h - padding} h${-padding} z`;
            break;
        case "loc-top":
            d = `M${w},0 h${w} v${padding} h${-w} z`;
            break;
        case "loc-top-right":
            d = `M${2 * w},0 h${w} v${h} h${-padding} v${-(h - padding)} h${-(w - padding)} z`;
            break;
        case "loc-right":
            d = `M${3 * w},${h} v${h} h${-padding} v${-h} z`;
            break;
        case "loc-bottom-right":
            d = `M${3 * w},${2 * h} v${h} h${-w} v${-padding} h${w - padding} v${-(h - padding)} z`;
            break;
        case "loc-bottom":
            d = `M${w},${3 * h} h${w} v${-padding} h${-w} z`;
            break;
        case "loc-bottom-left":
            d = `M0,${2 * h} h${padding} v${h - padding} h${w - padding} v${padding} h${-w} v${-h} z`;
            break;
        case "loc-left":
            d = `M0,${h} v${h} h${padding} v${-h} z`;
            break;
        default:
            console.error("Could not find location value for catcher: " + catcher);
    }

    catcher.setAttribute("d", d);
}

function getCatcherLocation(catcher) {
    let where;

    for (const value of catcher.classList.values()) {
        if (value.startsWith("loc-")) {
            where = value;
            break;
        }
    }

    return where;
}

function lineBorder(card, border) {
    let [bounds, padding] = getBoundsAndHalfPadding(card);
    const stroke = Math.ceil(parseFloat(border.style.strokeWidth)) / 2;

    // Define the four corner points
    const topLeft = { x: stroke, y: stroke };
    const topRight = { x: bounds.width - stroke, y: stroke };
    const bottomRight = { x: bounds.width - stroke, y: bounds.height - stroke };
    const bottomLeft = { x: stroke, y: bounds.height - stroke };

    let d = addBoxBorder(topLeft, topRight, bottomLeft, bottomRight);
    return [d, "", bounds, padding];
}

// Helper function to draw SVG borders with rounded corners
function addBoxBorder(topLeft, topRight, bottomLeft, bottomRight, radius = 5) {
    let d = "";

    // Start at top-left corner, slightly shifted right by radius
    d += `M${topLeft.x + radius},${topLeft.y} `;

    // Top side
    d = addLineSection(d, topRight.x - topLeft.x - 2 * radius, "horizontal", topLeft.x + radius, topLeft.y);

    // Top-right corner
    d += ` A${radius},${radius} 0 0 1 ${topRight.x},${topRight.y + radius}`;

    // Right side
    d = addLineSection(d, bottomRight.y - topRight.y - 2 * radius, "vertical", topRight.x, topRight.y + radius);

    // Bottom-right corner
    d += ` A${radius},${radius} 0 0 1 ${bottomRight.x - radius},${bottomRight.y}`;

    // Bottom side
    d = addLineSection(
        d,
        bottomLeft.x - bottomRight.x + 2 * radius,
        "horizontal",
        bottomRight.x - radius,
        bottomRight.y,
    );

    // Bottom-left corner
    d += ` A${radius},${radius} 0 0 1 ${bottomLeft.x},${bottomLeft.y - radius}`;

    // Left side
    d = addLineSection(d, topLeft.y - bottomLeft.y + 2 * radius, "vertical", bottomLeft.x, bottomLeft.y - radius);

    // Top-left corner
    d += ` A${radius},${radius} 0 0 1 ${topLeft.x + radius},${topLeft.y}`;
    return d;
}

function addLineSection(d, length, direction, startX, startY) {
    if (direction == "horizontal") {
        d += `M${startX},${startY} h${length}`;
    } else {
        d += `M${startX},${startY} v${length}`;
    }

    return d;
}
function doubleLineBorder(card, border) {
    let [bounds, padding] = getBoundsAndHalfPadding(card);
    const stroke = Math.ceil(parseFloat(border.style.strokeWidth)) / 2;

    let d = "";

    let spacing = Number.parseInt(border.style["stroke-width"]);
    spacing = Number.isNaN(spacing) ? 3 : spacing + 2;

    const outer = stroke;
    const inner = stroke + spacing;

    // Outer rectangle
    const outerTopLeft = { x: outer, y: outer };
    const outerTopRight = { x: bounds.width - outer, y: outer };
    const outerBottomRight = { x: bounds.width - outer, y: bounds.height - outer };
    const outerBottomLeft = { x: outer, y: bounds.height - outer };

    // Inner rectangle
    const innerTopLeft = { x: inner, y: inner };
    const innerTopRight = { x: bounds.width - inner, y: inner };
    const innerBottomRight = { x: bounds.width - inner, y: bounds.height - inner };
    const innerBottomLeft = { x: inner, y: bounds.height - inner };

    // Draw outer and inner borders
    let radius = 5;
    d += addBoxBorder(outerTopLeft, outerTopRight, outerBottomLeft, outerBottomRight, radius);
    d += addBoxBorder(innerTopLeft, innerTopRight, innerBottomLeft, innerBottomRight, radius - spacing);

    return [d, "", bounds, padding];
}

function wavyBorder(card) {
    let [bounds, padding] = getBoundsAndHalfPadding(card);

    let d = "";

    d = addWavySection(d, padding, bounds.width, "horizontal", "out", padding, padding);
    d = addWavySection(d, padding, bounds.width, "horizontal", "in", padding, bounds.height - padding);
    d = addWavySection(d, padding, bounds.height, "vertical", "out", padding, padding);
    d = addWavySection(d, padding, bounds.height, "vertical", "in", bounds.width - padding, padding);

    return [d, "wavy", bounds, padding];
}

function addWavySection(d, padding, length, direction, bend, startX, startY) {
    const b = bend == "out" ? -1 : 1;

    const l = length - 2 * padding;
    const nSteps = Math.floor(l / (2 * padding));
    const step = nSteps == 0 ? l : l / nSteps;

    if (direction == "horizontal") {
        d += `M${startX},${startY} q${step / 2},${b * padding} ${step},${0} `;
    } else {
        d += `M${startX},${startY} q${b * padding},${step / 2} ${0},${step} `;
    }

    for (let i = 1; i < nSteps; i++) {
        if (direction == "horizontal") {
            d += `t${step},0 `;
        } else {
            d += `t0,${step} `;
        }
    }

    return d;
}

function jaggedBorder(card) {
    let [bounds, padding] = getBoundsAndHalfPadding(card);

    padding *= 2;

    let d = "";

    d = addJaggedSection(d, padding, bounds.width, "horizontal", "out", padding, padding);
    d = addJaggedSection(d, padding, bounds.width, "horizontal", "in", padding, bounds.height - padding);
    d = addJaggedSection(d, padding, bounds.height, "vertical", "out", padding, padding);
    d = addJaggedSection(d, padding, bounds.height, "vertical", "in", bounds.width - padding, padding);

    return [d, "jagged", bounds, padding];
}

function addJaggedSection(d, padding, length, direction, bend, startX, startY) {
    const b = bend == "out" ? -1 : 1;

    let baseStep = padding;
    const amplitudeStep = padding;

    const l = length - 2 * padding;
    const nSteps = Math.ceil(l / baseStep);
    baseStep = nSteps == 0 ? l : l / nSteps;
    baseStep /= 2;

    d += `M${startX},${startY} `;

    for (let i = 0; i < nSteps; i++) {
        if (direction == "horizontal") {
            d += `l${baseStep},${b * amplitudeStep} l${baseStep},${b * -amplitudeStep} `;
        } else {
            d += `l${b * amplitudeStep},${baseStep} l${b * -amplitudeStep},${baseStep} `;
        }
    }

    return d;
}

function getBoundsAndHalfPadding(card) {
    const [scaleX, scaleY] = util.getScaleFactor(card);
    const bounds = card.getBoundingClientRect();

    const content = card.querySelector(".content");
    const css = content.currentStyle || window.getComputedStyle(content);
    let padding = Number.parseInt(css.paddingTop);
    padding = Number.isNaN(padding) || padding <= 0 ? 10 : padding;

    // we need to adjust the width/height by the scale factors because the browser will
    // scale the borders as well, with the assumption that the borders have been drawn
    // with the unscaled content size -> so always draw borders in unscaled coordinates
    const adjustedBounds = {
        width: bounds.width / scaleX,
        height: bounds.height / scaleY,
    };

    return [adjustedBounds, padding / 2];
}

module.exports.registerCardResizing = function (liveView) {
    let startX = 0;
    let startY = 0;

    let cardWidth = 0;
    let cardHeight = 0;

    let cardLeft = 0;
    let cardTop = 0;

    let card = null;

    let adjustWidth = null;
    let adjustHeight = null;

    let scaleX, scaleY;

    const updateCardSize = (e) => {
        e.stopImmediatePropagation();

        let left = cardLeft;
        let top = cardTop;

        let width = cardWidth;
        let height = cardHeight;

        if (adjustWidth == ADJUST_LEFT) {
            const diffX = e.clientX - startX;

            left = cardLeft + diffX;
            card.style.left = `${left}px`;

            width = cardWidth - diffX;
            card.style.width = `${width}px`;
        }

        if (adjustWidth == ADJUST_RIGHT) {
            const diffX = e.clientX - startX;
            width = cardWidth + diffX;
            card.style.width = `${width}px`;
        }

        if (adjustHeight == ADJUST_UP) {
            const diffY = e.clientY - startY;

            top = cardTop + diffY;
            card.style.top = `${top}px`;

            height = cardHeight - diffY;
            card.style.height = `${height}px`;
        }

        if (adjustHeight == ADJUST_DOWN) {
            const diffY = e.clientY - startY;
            height = cardHeight + diffY;
            card.style.height = `${height}px`;
        }

        renderBorder(card);
        viewRelations.positionRelations(liveView.el);

        return [left, top, width, height];
    };

    const onMouseUp = (e) => {
        let [left, top, width, height] = updateCardSize(e);

        window.removeEventListener("mousemove", updateCardSize);
        window.removeEventListener("mouseup", onMouseUp);

        let data = {
            id: card.dataset.id,
            type: "set_width_height_and_pos",
            width: width,
            height: height,
            x: left,
            y: top,
        };

        let cardsInside = viewUtils
            .getAllCards(liveView.el)
            .filter((each) => {
                return util.fullyContains(card.getBoundingClientRect(), each.getBoundingClientRect());
            })
            .filter((each) => each.dataset.id != data.id)
            .map((each) => {
                return {
                    id: each.dataset.id,
                    inViews: viewUtils.getIntersectingView(liveView.el, each).map((each) => each.dataset.id),
                };
            });

        data["inside_card_ids"] = cardsInside;

        console.log("module view_borders.js :: updated size", data);
        liveView.pushEvent("object-adjust-end", data);
    };

    const onMouseDown = (e) => {
        if (e.target.matches(SELECTOR_RESIZE_CATCHER)) {
            console.log("module view_borders.js :: start card resize");
            e.stopImmediatePropagation();

            card = util.findParentWithSelector(e.target, SELECTOR_CARD);

            window.addEventListener("mousemove", updateCardSize);
            window.addEventListener("mouseup", onMouseUp);

            startX = e.clientX;
            startY = e.clientY;

            const bounds = card.getBoundingClientRect();
            [scaleX, scaleY] = util.getScaleFactor(card);

            // The dimension of the card given by getBoundingClientRect() are already scaled
            // We want the not-scaled card-size, since by applying a new-size, the browser will scale
            // again. This solves the problem, that we scale once in JS and then CSS rescaled it again.
            cardWidth = bounds.width / scaleX;
            cardHeight = bounds.height / scaleY;

            cardLeft = parseInt(card.style.left);
            cardTop = parseInt(card.style.top);

            const where = getCatcherLocation(e.target);
            switch (where) {
                case "loc-top-left":
                    adjustWidth = ADJUST_LEFT;
                    adjustHeight = ADJUST_UP;
                    break;
                case "loc-top":
                    adjustWidth = null;
                    adjustHeight = ADJUST_UP;
                    break;
                case "loc-top-right":
                    adjustWidth = ADJUST_RIGHT;
                    adjustHeight = ADJUST_UP;
                    break;
                case "loc-right":
                    adjustWidth = ADJUST_RIGHT;
                    adjustHeight = null;
                    break;
                case "loc-bottom-right":
                    adjustWidth = ADJUST_RIGHT;
                    adjustHeight = ADJUST_DOWN;
                    break;
                case "loc-bottom":
                    adjustWidth = null;
                    adjustHeight = ADJUST_DOWN;
                    break;
                case "loc-bottom-left":
                    adjustWidth = ADJUST_LEFT;
                    adjustHeight = ADJUST_DOWN;
                    break;
                case "loc-left":
                    adjustWidth = ADJUST_LEFT;
                    adjustHeight = null;
                    break;
                default:
                    console.error("Could not find location for catcher, dragging to left and down: " + catcher);
                    adjustWidth = ADJUST_RIGHT;
                    adjustHeight = ADJUST_DOWN;
                    break;
            }
        }
    };

    // need to capture this event, because we are listening to the same events for dragging cards.
    // However if the user wants to resize, that should override the drag of the card
    liveView.el.addEventListener("mousedown", onMouseDown, { capture: true });
};

module.exports.updateBorders = function (view) {
    const cards = view.querySelectorAll(".view-card");
    cards.forEach(renderBorder);
};
